import { DotLottie } from "@lottiefiles/dotlottie-web";

const ROOT_SELECTOR = "[data-cirrus-trac-product-viewer]";

/**
 * @param {HTMLElement} root
 * @returns {DocumentFragment|HTMLElement}
 */
const clonePlusIcon = (root) => {
	const tpl = root.querySelector("[data-plus-icon-template]");
	if (tpl && "content" in tpl) {
		return /** @type {HTMLTemplateElement} */ (tpl).content.cloneNode(true);
	}
	const fallback = document.createElement("span");
	fallback.textContent = "+";
	fallback.className = "cb-block cb-text-lg cb-font-light cb-leading-none";
	fallback.setAttribute("aria-hidden", "true");
	return fallback;
};

/**
 * @param {HTMLElement} iconSlot
 * @param {boolean} isExpanded
 * @param {HTMLElement} root
 */
const setTabIcon = (iconSlot, isExpanded, root) => {
	iconSlot.textContent = "";
	if (!isExpanded) {
		iconSlot.appendChild(clonePlusIcon(root));
	}
};

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} src
 * @returns {DotLottie}
 */
const createDotLottie = (canvas, src) =>
	new DotLottie({
		canvas,
		src,
		loop: false,
		autoplay: true,
		layout: {
			fit: "contain",
			align: [0.5, 0.5],
		},
		backgroundColor: "transparent",
		renderConfig: {
			autoResize: true,
		},
	});

/**
 * @param {HTMLElement} root
 */
const cleanupTracProductViewer = (root) => {
	const lottieByCanvas = root._tracPvLottieByCanvas;
	if (lottieByCanvas instanceof Map) {
		lottieByCanvas.forEach((player) => {
			try {
				player.destroy();
			} catch {
				// ignore
			}
		});
		lottieByCanvas.clear();
	}
	delete root._tracPvLottieByCanvas;

	const onResize = root._tracPvOnResize;
	if (typeof onResize === "function") {
		window.removeEventListener("resize", onResize);
	}
	delete root._tracPvOnResize;

	const onRootClick = root._tracPvOnRootClick;
	if (typeof onRootClick === "function") {
		root.removeEventListener("click", onRootClick);
	}
	delete root._tracPvOnRootClick;

	const resizeTimer = root._tracPvResizeTimer;
	if (resizeTimer != null) {
		window.clearTimeout(resizeTimer);
	}
	delete root._tracPvResizeTimer;

	const imgBindings = root._tracPvPaneImgBindings;
	if (Array.isArray(imgBindings)) {
		imgBindings.forEach(({ img, fn }) => {
			img.removeEventListener("load", fn);
		});
	}
	delete root._tracPvPaneImgBindings;

	root.querySelectorAll("[data-visual-column]").forEach((col) => {
		if (col instanceof HTMLElement) {
			col.style.removeProperty("min-height");
		}
	});

	root.querySelectorAll("video[data-pane-video], [data-bg-vid]").forEach((vid) => {
		if (vid instanceof HTMLVideoElement) {
			try {
				vid.pause();
			} catch {
				// ignore
			}
		}
	});

	root.dataset.tracPvInitialized = "false";
};

/**
 * @param {import("@lottiefiles/dotlottie-web").DotLottie} player
 */
const playLottieFromStart = (player) => {
	try {
		if (typeof player.stop === "function") {
			player.stop();
		} else if (typeof player.setFrame === "function") {
			player.setFrame(0);
		}
		player.play();
	} catch {
		// ignore
	}
};

/**
 * @param {HTMLElement} root
 */
const initializeTracProductViewer = (root) => {
	if (root.dataset.tracPvInitialized === "true") {
		cleanupTracProductViewer(root);
	}

	const count = parseInt(root.dataset.itemCount || "0", 10);
	if (!Number.isFinite(count) || count < 1) {
		return;
	}

	const tabs = Array.from(root.querySelectorAll("[data-item-tab]"));
	const panes = Array.from(root.querySelectorAll("[data-item-pane]"));
	const prevBtns = Array.from(root.querySelectorAll('[data-direction="prev"]'));
	const nextBtns = Array.from(root.querySelectorAll('[data-direction="next"]'));
	const bgLayer = root.querySelector("[data-bg-layer]");
	const bgImg = root.querySelector("[data-bg-img]");
	const bgVideo = root.querySelector("[data-bg-vid]");
	const visualColumn = root.querySelector("[data-visual-column]");
	const mqlLg = window.matchMedia("(min-width: 1024px)");

	if (!tabs.length || tabs.length !== count) {
		return;
	}

	/** @type {Map<HTMLCanvasElement, import("@lottiefiles/dotlottie-web").DotLottie>} */
	const lottieByCanvas = new Map();
	root._tracPvLottieByCanvas = lottieByCanvas;

	let defaultActiveIndex = parseInt(root.dataset.initialActive || "0", 10);
	if (!Number.isFinite(defaultActiveIndex) || defaultActiveIndex < 0 || defaultActiveIndex >= count) {
		defaultActiveIndex = 0;
	}
	let activeIndex = defaultActiveIndex;

	const setInteracted = (value) => {
		root.dataset.hasInteracted = value ? "true" : "false";
		if (!value) {
			root.dataset.mobileCopyVisible = "false";
		}
		syncTabIcons();
	};

	const setMobileCopyVisible = (value) => {
		root.dataset.mobileCopyVisible = value ? "true" : "false";
		syncTabIcons();
	};

	const syncTabIcons = () => {
		const hasInteracted = root.dataset.hasInteracted === "true";
		const mobileCopyVisible = root.dataset.mobileCopyVisible === "true";

		tabs.forEach((tab, i) => {
			const isActive = i === activeIndex;
			const iconSlot = tab.querySelector("[data-tab-icon]");
			if (iconSlot instanceof HTMLElement) {
				setTabIcon(iconSlot, isActive && hasInteracted, root);
			}
		});

		root.querySelectorAll("[data-mobile-item-tab]").forEach((tab) => {
			if (!(tab instanceof HTMLElement)) {
				return;
			}
			const idx = parseInt(tab.dataset.itemIndex || "-1", 10);
			const isActive = idx === activeIndex;
			const iconSlot = tab.querySelector("[data-tab-icon]");
			if (iconSlot instanceof HTMLElement) {
				setTabIcon(
					iconSlot,
					isActive && hasInteracted && mobileCopyVisible,
					root
				);
			}
		});
	};

	const applyBgFocal = (pane) => {
		if (!(bgImg instanceof HTMLImageElement) || !(pane instanceof HTMLElement)) {
			return;
		}
		const mobile = pane.getAttribute("data-bg-focal-mobile") || "50% 50%";
		const desktop = pane.getAttribute("data-bg-focal-desktop") || mobile;
		bgImg.style.setProperty("--trac-pv-focal-mobile", mobile);
		bgImg.style.setProperty("--trac-pv-focal-desktop", desktop);
	};

	const clearBgFocal = () => {
		if (!(bgImg instanceof HTMLImageElement)) {
			return;
		}
		bgImg.style.removeProperty("--trac-pv-focal-mobile");
		bgImg.style.removeProperty("--trac-pv-focal-desktop");
	};

	const hideBgImage = () => {
		if (bgImg instanceof HTMLImageElement) {
			bgImg.removeAttribute("src");
			clearBgFocal();
			bgImg.classList.add("cb-hidden");
		}
	};

	const hideBgVideo = () => {
		if (bgVideo instanceof HTMLVideoElement) {
			try {
				bgVideo.pause();
			} catch {
				// ignore
			}
			bgVideo.removeAttribute("src");
			bgVideo.classList.add("cb-hidden");
		}
	};

	const syncBackground = (index) => {
		if (!bgLayer) {
			return;
		}
		const pane = panes[index];
		if (!pane) {
			return;
		}
		const vType = pane.getAttribute("data-visual-type") || "";
		const imgUrl = pane.getAttribute("data-bg-image") || "";
		const vidUrl = pane.getAttribute("data-bg-video") || "";

		if (vType === "image_background" && vidUrl && bgVideo instanceof HTMLVideoElement) {
			hideBgImage();
			if (bgVideo.getAttribute("src") !== vidUrl) {
				bgVideo.setAttribute("src", vidUrl);
			}
			bgVideo.classList.remove("cb-hidden");
			const playback = bgVideo.play();
			if (playback && typeof playback.catch === "function") {
				playback.catch(() => {});
			}
			bgLayer.classList.remove("cb-hidden");
		} else if (vType === "image_background" && imgUrl && bgImg instanceof HTMLImageElement) {
			hideBgVideo();
			bgImg.setAttribute("src", imgUrl);
			bgImg.setAttribute("alt", "");
			applyBgFocal(pane);
			bgImg.classList.remove("cb-hidden");
			bgLayer.classList.remove("cb-hidden");
		} else {
			hideBgImage();
			hideBgVideo();
			bgLayer.classList.add("cb-hidden");
		}
	};

	const syncPaneVideos = (index) => {
		panes.forEach((pane, i) => {
			const vid = pane.querySelector("video[data-pane-video]");
			if (!(vid instanceof HTMLVideoElement)) {
				return;
			}
			if (i === index) {
				const playback = vid.play();
				if (playback && typeof playback.catch === "function") {
					playback.catch(() => {});
				}
			} else {
				try {
					vid.pause();
				} catch {
					// ignore
				}
			}
		});
	};

	const ensureLottie = (index) => {
		const pane = panes[index];
		if (!pane || pane.getAttribute("data-visual-type") !== "lottie") {
			return;
		}
		const canvas = pane.querySelector("[data-lottie-canvas]");
		if (!(canvas instanceof HTMLCanvasElement)) {
			return;
		}
		const src = canvas.dataset.lottieSrc || "";
		if (!src) {
			return;
		}

		const existing = lottieByCanvas.get(canvas);
		if (existing) {
			playLottieFromStart(existing);
			requestAnimationFrame(() => {
				try {
					existing.resize();
				} catch {
					// ignore
				}
				syncVisualColumnMinHeight();
			});
			return;
		}

		const player = createDotLottie(canvas, src);
		lottieByCanvas.set(canvas, player);
		requestAnimationFrame(() => {
			try {
				player.resize();
			} catch {
				// ignore
			}
			syncVisualColumnMinHeight();
		});
	};

	const syncVisualColumnMinHeight = () => {
		if (mqlLg.matches) {
			if (visualColumn instanceof HTMLElement) {
				visualColumn.style.removeProperty("min-height");
			}
			return;
		}
		if (!(visualColumn instanceof HTMLElement) || panes.length === 0) {
			return;
		}
		const colW = visualColumn.clientWidth;
		if (colW < 1) {
			return;
		}
		const currentActive = parseInt(root.dataset.initialActive || "0", 10);
		let maxH = 0;
		panes.forEach((pane) => {
			pane.classList.remove("cb-hidden");
			Object.assign(pane.style, {
				boxSizing: "border-box",
				visibility: "hidden",
				position: "absolute",
				left: "0",
				top: "0",
				width: `${colW}px`,
				height: "auto",
				pointerEvents: "none",
			});
		});
		panes.forEach((pane) => {
			maxH = Math.max(maxH, pane.offsetHeight);
		});
		panes.forEach((pane, i) => {
			pane.removeAttribute("style");
			pane.classList.toggle("cb-hidden", i !== currentActive);
		});
		if (maxH > 0) {
			visualColumn.style.minHeight = `${maxH}px`;
		}
	};

	/**
	 * @param {number} index
	 * @param {{ enterInteracted?: boolean, showMobileCopy?: boolean }} [options]
	 */
	const setActive = (index, options = {}) => {
		if (options.enterInteracted === true) {
			setInteracted(true);
			if (!mqlLg.matches && options.showMobileCopy !== false) {
				setMobileCopyVisible(true);
			}
		} else if (options.enterInteracted === false) {
			setInteracted(false);
		} else if (
			!mqlLg.matches &&
			root.dataset.hasInteracted === "true" &&
			options.showMobileCopy !== false
		) {
			setMobileCopyVisible(true);
		}

		const next = Math.max(0, Math.min(count - 1, index));
		activeIndex = next;
		root.dataset.initialActive = String(next);

		tabs.forEach((tab, i) => {
			const isActive = i === next;
			tab.setAttribute("data-active", isActive ? "true" : "false");
			tab.setAttribute("aria-selected", isActive ? "true" : "false");
		});

		root.querySelectorAll("[data-mobile-item-tab]").forEach((tab) => {
			if (!(tab instanceof HTMLElement)) {
				return;
			}
			const idx = parseInt(tab.dataset.itemIndex || "-1", 10);
			const isActive = idx === next;
			tab.setAttribute("data-active", isActive ? "true" : "false");
			tab.setAttribute("aria-selected", isActive ? "true" : "false");
		});

		syncTabIcons();

		panes.forEach((pane, i) => {
			const isActive = i === next;
			pane.setAttribute("data-active", isActive ? "true" : "false");
			pane.setAttribute("aria-hidden", isActive ? "false" : "true");
			pane.classList.toggle("cb-hidden", !isActive);
		});

		root.querySelectorAll("[data-mobile-overlay]").forEach((el) => {
			if (!(el instanceof HTMLElement)) {
				return;
			}
			const idx = parseInt(el.dataset.itemIndex || "-1", 10);
			el.classList.toggle("cb-hidden", idx !== next);
		});

		syncBackground(next);
		syncPaneVideos(next);
		ensureLottie(next);

		prevBtns.forEach((btn) => {
			if (btn instanceof HTMLButtonElement) {
				btn.disabled = next <= 0;
			}
		});
		nextBtns.forEach((btn) => {
			if (btn instanceof HTMLButtonElement) {
				btn.disabled = next >= count - 1;
			}
		});

		syncVisualColumnMinHeight();
	};

	const onResize = () => {
		window.clearTimeout(root._tracPvResizeTimer);
		root._tracPvResizeTimer = window.setTimeout(() => {
			syncVisualColumnMinHeight();
			lottieByCanvas.forEach((p) => {
				try {
					p.resize();
				} catch {
					// ignore
				}
			});
		}, 100);
	};
	window.addEventListener("resize", onResize);
	root._tracPvOnResize = onResize;

	const onRootClick = (event) => {
		const target = event.target instanceof Element ? event.target : null;
		if (!target) {
			return;
		}
		const mobileCopyScrim = target.closest("[data-mobile-copy-scrim]");
		if (mobileCopyScrim instanceof HTMLElement && root.contains(mobileCopyScrim)) {
			if (!mqlLg.matches) {
				setMobileCopyVisible(false);
			}
			return;
		}
		const mobileCloseBtn = target.closest("[data-close-mobile-copy]");
		if (mobileCloseBtn instanceof HTMLElement && root.contains(mobileCloseBtn)) {
			if (!mqlLg.matches) {
				setMobileCopyVisible(false);
			}
			return;
		}
		const closeBtn = target.closest("[data-close-viewer]");
		if (closeBtn instanceof HTMLElement && root.contains(closeBtn)) {
			if (mqlLg.matches) {
				setInteracted(false);
				setActive(defaultActiveIndex);
			}
			return;
		}
		const tab = target.closest("[data-item-tab]");
		if (tab instanceof HTMLElement && root.contains(tab)) {
			const idx = parseInt(tab.dataset.itemIndex || "-1", 10);
			if (Number.isFinite(idx) && idx >= 0 && idx < count) {
				setActive(idx, { enterInteracted: true });
			}
			return;
		}
		const mobileTab = target.closest("[data-mobile-item-tab]");
		if (mobileTab instanceof HTMLElement && root.contains(mobileTab)) {
			const idx = parseInt(mobileTab.dataset.itemIndex || "-1", 10);
			if (Number.isFinite(idx) && idx >= 0 && idx < count) {
				if (
					!mqlLg.matches &&
					root.dataset.hasInteracted === "true" &&
					idx === activeIndex &&
					root.dataset.mobileCopyVisible !== "true"
				) {
					setMobileCopyVisible(true);
					return;
				}
				setActive(idx, { enterInteracted: true });
			}
			return;
		}
		const dirBtn = target.closest("[data-direction]");
		if (dirBtn instanceof HTMLElement && root.contains(dirBtn)) {
			const dir = dirBtn.getAttribute("data-direction");
			if (dir === "prev") {
				setActive(activeIndex - 1, { enterInteracted: true });
			} else if (dir === "next") {
				setActive(activeIndex + 1, { enterInteracted: true });
			}
		}
	};
	root.addEventListener("click", onRootClick);
	root._tracPvOnRootClick = onRootClick;

	/** @type {{ img: HTMLImageElement; fn: EventListener }[]} */
	const paneImgBindings = [];
	root.querySelectorAll("[data-item-pane] img").forEach((img) => {
		if (!(img instanceof HTMLImageElement)) {
			return;
		}
		if (img.complete) {
			return;
		}
		const fn = () => {
			syncVisualColumnMinHeight();
		};
		img.addEventListener("load", fn, { once: true });
		paneImgBindings.push({ img, fn });
	});
	root._tracPvPaneImgBindings = paneImgBindings;

	root.dataset.tracPvInitialized = "true";
	setInteracted(true);
	if (!mqlLg.matches) {
		setMobileCopyVisible(true);
	}
	setActive(activeIndex);
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			syncVisualColumnMinHeight();
		});
	});
};

/**
 * @param {ParentNode|HTMLElement|Document|ArrayLike<HTMLElement>|undefined|null} [context]
 * @returns {ParentNode}
 */
const getSearchRoot = (context) => {
	if (!context) {
		return document;
	}
	if (
		context.nodeType === 1 ||
		context.nodeType === 9 ||
		context.nodeType === 11
	) {
		return /** @type {HTMLElement|Document|DocumentFragment} */ (context);
	}
	const first = /** @type {ArrayLike<HTMLElement>} */ (context)[0];
	return first || document;
};

/**
 * @param {ParentNode|HTMLElement|Document|ArrayLike<HTMLElement>|undefined|null} [context]
 */
const initializeAll = (context) => {
	const node = getSearchRoot(context);
	node.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
		if (el instanceof HTMLElement) {
			initializeTracProductViewer(el);
		}
	});
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => initializeAll());
} else {
	initializeAll();
}

if (window.acf) {
	window.acf.addAction("render_block_preview", initializeAll);
	window.acf.addAction("render_block_preview/type=trac-product-viewer", initializeAll);
	window.acf.addAction("render_block_preview/type=cirrus/trac-product-viewer", initializeAll);
}
